import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

export interface User {
  id: number;
  username: string;
  email: string;
  mobile: string;
  customer_id: number;
  permissions: Array<string>;
  is_superuser: boolean;
  sites: Array<any>;
  site_id: number;
}

export interface UserAuthInfo {
  errors: Array<string>;
  user: User;
  isAuthenticated: boolean;
}

@Module
export default class AuthModule extends VuexModule implements UserAuthInfo {
  errors = [];
  user = {} as User;
  isAuthenticated = !!JwtService.getToken();

  /**
   * Get current user object
   * @returns User
   */
  get currentUser(): User {
    return this.user;
  }

  /**
   * Verify user authentication
   * @returns boolean
   */
  get isUserAuthenticated(): boolean {
    return this.isAuthenticated;
  }

  /**
   * Get authentification errors
   * @returns array
   */
  get getErrors(): Array<string> {
    return this.errors;
  }

  @Mutation
  [Mutations.SET_ERROR](error) {
    this.errors = error;
  }

  @Mutation
  [Mutations.SET_AUTH](auth) {
    this.isAuthenticated = true;
    this.errors = [];
    JwtService.saveToken(auth.access);
    if(auth.refresh) JwtService.saveRefresh(auth.refresh);
  }

  @Mutation
  [Mutations.SET_USER](user) {
    // 检查默认站点是否存在权限列表中，不在则让用户重新选择默认站点
    let site = user.sites.find((s) => {
      return s.id == user.site_id;
    });
    user.site_id = site ? site.id : '';
    this.user = user;
  }

  @Mutation
  [Mutations.SET_SITE](site_id) {
    this.user.site_id = site_id;
  }

  @Mutation
  [Mutations.PURGE_AUTH]() {
    this.isAuthenticated = false;
    this.user = {} as User;
    this.errors = [];
    JwtService.destroyToken();
  }

  @Action
  [Actions.LOGIN](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("login", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [response.data.detail]);
          reject();
        });
    });
  }

  @Action
  [Actions.LOGOUT]() {
    this.context.commit(Mutations.PURGE_AUTH);
  }

  @Action  // @暂未实现
  [Actions.REGISTER](credentials) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("registration", credentials)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_AUTH, data);
          // @获取用户信息，是否能够直接获取待后续的注册开通流程
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [response.data.detail]);
          reject();
        });
    });
  }

  @Action  // @暂未实现
  [Actions.FORGOT_PASSWORD](payload) {
    return new Promise<void>((resolve, reject) => {
      ApiService.post("forgot_password", payload)
        .then(({ data }) => {
          // @发邮件或短信，再通过验证码到站点修改密码的流程
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, [response.data.detail]);
          reject();
        });
    });
  }

  @Action
  [Actions.GET_USER]() {
    return new Promise<void>((resolve, reject) => {
      if(!JwtService.getToken()) {
        JwtService.destroyToken();
        JwtService.destroyRefresh();
        location.href="/#sign-in"
      } else {
        ApiService.setHeader();
        ApiService.get("user/info")
        .then(({ data }) => {
          this.context.commit(Mutations.SET_USER, data);
          resolve();
        })
      }
    });
  }

  @Action
  [Actions.UPDATE_SITE](site_id) {
    return new Promise<void>((resolve, reject) => {
      ApiService.patch('user/' + this.user.id, {site_id: site_id}).then(() => {
        this.context.commit(Mutations.SET_SITE, site_id);
      })
    });
  }
}
